import { useState, useMemo } from 'react';
import { Box, Button, Paper, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { Plus, MoreVertical } from 'lucide-react';
import { useAuthStore } from '@lingaai/user-management';
import { formatDate } from '@lingaai/shared-utils';
import type { User } from '@lingaai/types';
import { resetUserPassword, deactivateUser, activateUser, deleteUser } from '../../api/users';
import { useUsers } from '../../hooks/useUsers';

interface UserListProps {
  onAddUser?: () => void;
  onEditUser?: (user: User) => void;
}

export function UserList({ onAddUser, onEditUser }: UserListProps) {
  const currentUser = useAuthStore((state) => state.user);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  // Fetch users with server-side search, sort, and pagination
  const sortField = sortModel[0]?.field || 'created_at';
  const sortOrder = sortModel[0]?.sort || 'desc';
  const {
    data: usersResponse,
    isLoading,
    refetch,
  } = useUsers(currentUser?.tenant_id, {
    search: search || undefined,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    sortBy: sortField,
    sortOrder,
  });

  const users = usersResponse?.data || [];
  const totalRows = usersResponse?.pagination?.total || 0;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedUser here - it's needed for dialogs
  };

  const handleAction = async (action: () => Promise<void>) => {
    try {
      await action();
      await refetch();
      handleMenuClose();
    } catch (error) {
      console.error('Action failed:', error);
      // TODO: Show error toast
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'first_name',
        headerName: 'First Name',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Box onClick={() => onEditUser?.(params.row as User)} sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            {params.value}
          </Box>
        ),
      },
      {
        field: 'last_name',
        headerName: 'Last Name',
        minWidth: 120,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'organization',
        headerName: 'Organization',
        minWidth: 150,
      },
      {
        field: 'role',
        headerName: 'Role',
        minWidth: 120,
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 100,
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 120,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row as User)}>
            <MoreVertical size={18} />
          </IconButton>
        ),
      },
    ],
    [onEditUser]
  );

  return (
    <Box>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField fullWidth placeholder="Search users by name, email, or organization..." value={search} onChange={(e) => setSearch(e.target.value)} size="small" />
          {onAddUser && (
            <Button variant="contained" startIcon={<Plus size={16} />} onClick={onAddUser} size="small" sx={{ whiteSpace: 'nowrap' }}>
              Add User
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedUser && handleAction(() => resetUserPassword(selectedUser.id))}>Reset Password</MenuItem>
        {selectedUser?.status === 'approved' && (
          <MenuItem
            onClick={() => {
              setDeactivateDialogOpen(true);
              handleMenuClose();
            }}
          >
            Deactivate
          </MenuItem>
        )}
        {selectedUser?.status === 'deactivated' && <MenuItem onClick={() => selectedUser && handleAction(() => activateUser(selectedUser.id))}>Activate</MenuItem>}
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => {
          setDeactivateDialogOpen(false);
          setSelectedUser(null);
        }}
      >
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate {selectedUser?.first_name} {selectedUser?.last_name}? They will be unable to log in until reactivated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeactivateDialogOpen(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedUser) {
                handleAction(() => deactivateUser(selectedUser.id));
                setDeactivateDialogOpen(false);
                setSelectedUser(null);
              }
            }}
            color="warning"
            variant="contained"
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete {selectedUser?.first_name} {selectedUser?.last_name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedUser) {
                handleAction(() => deleteUser(selectedUser.id));
                setDeleteDialogOpen(false);
                setSelectedUser(null);
              }
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
